import { useEffect, useRef, useSyncExternalStore } from "react";

/** Which action sheet is up on the gaps-first home. */
export enum GapsSheetKind {
	PostShift = "post-shift",
	AddStaff = "add-staff",
	StaffActions = "staff-actions",
	AssignUnit = "assign-unit",
	CallIn = "call-in",
	PostedActions = "posted-actions",
	ApproveConfirm = "approve-confirm",
}

/** Why the assign-unit sheet opened — one sheet serves all three (P3-6), and its analytics say which. */
export enum GapsAssignUnitReason {
	StaffRow = "staff_row",
	Approve = "approve",
	Add = "add",
}

/**
 * The hole an add was started from, captured at the tap (P3-8): the group by key, and the unit and hours the
 * missing row named. Captured by VALUE because a missing row's own key is positional — the unit's second hole
 * becomes its first once someone fills one — while the unit and window the user chose must not move under them.
 */
export type GapsAddStaffScope = { groupKey: string; locationName: string; startMin: number; endMin: number };

/**
 * The staffing day a home sheet acts on, as the home keys its URL day (`yyyy-MM-dd`). Every sheet that reads or writes
 * the home's day — a group, a shift, a hole, the day's staff — carries the day it was opened for, so a sheet can never
 * act on a day the user did not open it on: the home's day can move under an open sheet (the bar's Gaps slot lands on
 * the undated home, the phone's back button steps the URL), and a group or a hole is keyed without a day.
 */
type DayBound = { dayISO: string };

/**
 * What a sheet was opened for. Anything that can change while it is open — a group's counts, a shift, a request —
 * is carried by id or key and read fresh from the page's data, never snapshotted here. `null` fields are the
 * unscoped openings: the bottom bar's Post and More's quick actions start from nothing.
 *
 * The posted-request sheets carry no day: a request carries its own, and they act on it wherever it is listed.
 */
export type GapsSheetIntent =
	| ({ kind: GapsSheetKind.PostShift; groupKey: string | null } & DayBound)
	| ({ kind: GapsSheetKind.AddStaff; scope: GapsAddStaffScope | null; linkedRequestId: string | null } & DayBound)
	| ({ kind: GapsSheetKind.StaffActions; shiftId: string } & DayBound)
	| ({ kind: GapsSheetKind.AssignUnit; shiftId: string; reason: GapsAssignUnitReason } & DayBound)
	| ({ kind: GapsSheetKind.CallIn } & DayBound)
	| { kind: GapsSheetKind.PostedActions; requestId: string }
	| { kind: GapsSheetKind.ApproveConfirm; requestId: string; nurseId: number };

/** One kind's intent — the target a sheet of that kind is handed. */
export type GapsSheetIntentOf<K extends GapsSheetKind> = Extract<GapsSheetIntent, { kind: K }>;

/**
 * The intent a home on `dayISO` shows: a sheet opened for another day is not this home's to draw. It is the same-render
 * half of the day rule — the store half is `useCloseGapsSheetOffDay` — so a sheet never draws, even for a frame, over a
 * day it was not opened on. `null` passes through.
 */
export const intentForHomeDay = (intent: GapsSheetIntent | null, dayISO: string): GapsSheetIntent | null =>
	intent !== null && "dayISO" in intent && intent.dayISO !== dayISO ? null : intent;

/**
 * The one open intent, module-level, and everything reading it.
 *
 * The sheets live in `MobileGapsStaffing`, but two of their triggers do not: the bottom bar's Post slot and More's
 * quick actions sit in the layout, in a different subtree, and may fire from another route. React state in either
 * place could not reach the other; an external store makes one write visible to every reader in the same commit —
 * the pattern `useGapsViewPreference` uses for the view choice. It also lets the bar light its Post slot while the
 * post sheet is open.
 *
 * It outlives the page on purpose: a trigger on `/requests` opens the intent and then navigates home, and the page
 * that mounts must find it. The flip side is the host's job: the page that renders the sheets has to close the
 * intent when it unmounts, or a sheet left open would reappear on the next visit — another department's included.
 */
let currentIntent: GapsSheetIntent | null = null;
const subscribers = new Set<() => void>();

const notifySubscribers = (): void => {
	for (const onChange of subscribers) onChange();
};

const subscribe = (onChange: () => void): (() => void) => {
	subscribers.add(onChange);
	return () => {
		subscribers.delete(onChange);
	};
};

/** The open intent, or `null` when no sheet is up. Stable between writes, as `useSyncExternalStore` requires. */
export const getGapsSheetIntent = (): GapsSheetIntent | null => currentIntent;

/** Opens a sheet, replacing whichever was up — the home shows one action sheet at a time. */
export const openGapsSheet = (intent: GapsSheetIntent): void => {
	currentIntent = intent;
	notifySubscribers();
};

/** Closes whichever sheet is up. Closing when none is up is a no-op and re-renders nobody. */
export const closeGapsSheet = (): void => {
	if (currentIntent === null) return;
	currentIntent = null;
	notifySubscribers();
};

/**
 * Closes the sheet only while `intent` is still the one up. A sheet's own close is this, never `closeGapsSheet`: a
 * write that finishes after the user moved on — to another sheet from the bar, or to none — must not close the sheet
 * they moved to.
 */
export const closeGapsSheetIfCurrent = (intent: GapsSheetIntent): void => {
	if (currentIntent === intent) closeGapsSheet();
};

/**
 * Hands `from` on to `next` (an add's assign-unit, an approval's) only while `from` is still up; `false` when the
 * user already moved on, and nothing was opened.
 */
export const replaceGapsSheetIfCurrent = (from: GapsSheetIntent, next: GapsSheetIntent): boolean => {
	if (currentIntent !== from) return false;
	openGapsSheet(next);
	return true;
};

export type GapsSheetController = {
	intent: GapsSheetIntent | null;
	open: (intent: GapsSheetIntent) => void;
	close: () => void;
};

/** The open sheet intent for any component, re-rendering it when a sheet opens, changes or closes. */
export const useGapsSheet = (): GapsSheetController => {
	const intent = useSyncExternalStore(subscribe, getGapsSheetIntent);
	return { intent, open: openGapsSheet, close: closeGapsSheet };
};

/**
 * The host's half of the bargain above: closes whichever sheet is up once the page that renders the sheets unmounts
 * for real, so a sheet left open never reappears on the next visit — or in another department, whose home is a new
 * mount (`MobileGapsShell` keys it by department). A host that renders only some kinds (Open Shifts: the posted-shift
 * and approve sheets) passes them, and closes only an intent of its own.
 *
 * Deferred by a microtask and re-checked, because a plain cleanup would also fire on StrictMode's simulated unmount
 * (`index.tsx` renders under `StrictMode`): React tears the effect down and runs it again synchronously, and a close
 * there would drop the intent a trigger on another route opened before navigating home — exactly the case the store
 * outlives the page for. The ref survives the simulated remount, so only an unmount that is never followed by a
 * remount closes the sheet.
 */
export const useCloseGapsSheetOnUnmount = (ownedKinds?: readonly GapsSheetKind[]): void => {
	const isMountedRef = useRef<boolean>(false);
	// Pass a module-level constant: a new list per render would re-run the effect (harmlessly — the re-check below keeps
	// the sheet — but pointlessly).
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			queueMicrotask(() => {
				if (isMountedRef.current) return;
				// A host that renders only some kinds leaves the others alone: Open Shifts hands an approval to the home's
				// assign-unit sheet and navigates there, and its own unmount must not close the sheet it just opened.
				if (ownedKinds !== undefined && (currentIntent === null || !ownedKinds.includes(currentIntent.kind))) return;
				closeGapsSheet();
			});
		};
	}, [ownedKinds]);
};

/**
 * The store half of the day rule (see `DayBound`): once the home's day CHANGES under an open sheet that was opened for
 * another day, that sheet is closed — so returning to its day does not bring it back, and nothing acts on a day the user
 * left. Only a change counts: an intent opened for a day the home is navigating to (an approval handing on to the
 * request's day, a quick action from another route) is written BEFORE the navigation lands, and must survive until it
 * does.
 */
export const useCloseGapsSheetOffDay = (dayISO: string): void => {
	const shownDayRef = useRef<string>(dayISO);
	useEffect(() => {
		if (shownDayRef.current === dayISO) return;
		shownDayRef.current = dayISO;
		if (currentIntent !== null && "dayISO" in currentIntent && currentIntent.dayISO !== dayISO) closeGapsSheet();
	}, [dayISO]);
};
